import React from 'react'
import styled from 'styled-components'
import { useReadOnly, useSlateStatic } from 'vendor/slate-react'
import { LEAF_TYPES } from 'cg/components/documents/editor/slate/types'
import { SUGGESTION_ROOT, SUGGESTION_TYPES } from 'cg/components/documents/editor/slate/plugins/suggestions'
import { getCommentThreadIdsFromLeaf } from 'cg/components/documents/editor/slate/plugins/comments'
import { DocumentInlineCode as Code } from 'cg/components/documents/editor/slate/documentStyles'

const Leaf = ({ attributes, children, leaf, activeCommentThreadIds, activeSuggestionId }) => {
  // Suggestions and comments are editing-only overlays. In read-only/published
  // we never surface their styling: comments render as plain text, and pending
  // suggestions are resolved away (insertions hidden, deletions kept as text).
  const readOnly = useReadOnly()
  const editor = useSlateStatic()

  const suggestionKeys = leaf[SUGGESTION_ROOT]
    ? Object.keys(leaf).filter(key => key.startsWith('suggestion_') && key !== 'suggestion_transient')
    : []
  const suggestionData = suggestionKeys.length > 0 ? leaf[suggestionKeys[suggestionKeys.length - 1]] : null
  const suggestionType = suggestionData?.type
  const isSuggestionActive = !!suggestionData?.id && suggestionData.id === activeSuggestionId

  // Clicking a tracked change opens its block's panel focused on that
  // suggestion (parity with comment highlights). UI-agnostic: the handler is
  // wired by editor.js and defaults to a no-op. Stop propagation so an
  // overlapping comment mark's handler doesn't override the focused suggestion.
  const handleSuggestionClick = event => {
    if (!suggestionData?.id) return
    event.stopPropagation()
    editor.openSuggestion?.(suggestionData.id)
  }

  // Read-only/published renders the *accepted* document: never show track-change
  // styling, never leak unaccepted changes. A pending insertion is an unaccepted
  // addition → hide it entirely; a pending deletion is unaccepted → keep the
  // (still-present) text as plain. Edit mode shows the track-change styling.
  let hideLeaf = false
  if (suggestionType === SUGGESTION_TYPES.remove) {
    if (!readOnly) {
      children = (
        <SuggestionRemove {...attributes} $isActive={isSuggestionActive} onClick={handleSuggestionClick}>
          {children}
        </SuggestionRemove>
      )
    }
  } else if (suggestionType === SUGGESTION_TYPES.insert) {
    if (readOnly) {
      hideLeaf = true
    } else {
      children = (
        <SuggestionInsert {...attributes} $isActive={isSuggestionActive} onClick={handleSuggestionClick}>
          {children}
        </SuggestionInsert>
      )
    }
  }
  if (leaf[LEAF_TYPES.bold]) {
    children = <strong {...attributes}>{children}</strong>
  }

  if (leaf[LEAF_TYPES.code]) {
    children = <Code {...attributes}>{children}</Code>
  }
  if (leaf[LEAF_TYPES.italic]) {
    children = <em {...attributes}>{children}</em>
  }

  if (leaf[LEAF_TYPES.underlined]) {
    children = <u {...attributes}>{children}</u>
  }

  if (leaf[LEAF_TYPES.strikethrough]) {
    children = <strike {...attributes}>{children}</strike>
  }

  if (leaf[LEAF_TYPES.superscript]) {
    children = <sup {...attributes}>{children}</sup>
  }

  if (leaf[LEAF_TYPES.subscript]) {
    children = <sub {...attributes}>{children}</sub>
  }

  if (leaf[LEAF_TYPES.mark]) {
    children = (
      <Mark style={{ background: `${leaf[LEAF_TYPES.mark]}` }} {...attributes}>
        {children}
      </Mark>
    )
  }

  if (leaf[LEAF_TYPES.color]) {
    children = (
      <Color
        style={{
          color: `${leaf[LEAF_TYPES.color]}`
        }}
        {...attributes}
      >
        {children}
      </Color>
    )
  }

  const commentThreadIds = readOnly ? [] : getCommentThreadIdsFromLeaf(leaf)
  if (commentThreadIds.length > 0) {
    const isActive =
      Array.isArray(activeCommentThreadIds) && activeCommentThreadIds.some(id => commentThreadIds.includes(id))

    // A leaf can carry marks for multiple overlapping threads, but the panel
    // works one thread at a time so per-thread actions (delete/discard) stay
    // unambiguous. Open the topmost (most recently added) thread on this leaf.
    const topThreadId = commentThreadIds[commentThreadIds.length - 1]

    children = (
      <CommentMark
        $isActive={isActive}
        onClick={event => {
          event.stopPropagation()
          editor.openCommentThread?.([topThreadId])
        }}
      >
        {children}
      </CommentMark>
    )
  }

  return (
    <span
      {...attributes}
      className={`${leaf.className ? leaf.className : ''}`}
      style={hideLeaf ? { display: 'none' } : undefined}
    >
      {children}
    </span>
  )
}

export default Leaf

const Mark = styled.span`
  padding: 0px;
`
const Color = styled.span``

const ANNOTATION_PURPLE = '#884ffc'
const annotationBackground = (props, active = false) => {
  if (props.theme.id === 'dark') return !active ? 'rgba(136, 79, 252, 0.06)' : 'rgba(136, 79, 252, 0.1)'
  return !active ? 'rgba(136, 79, 252, 0.05)' : 'rgba(136, 79, 252, 0.1)'
}

const SuggestionRemove = styled.span`
  text-decoration: line-through;
  text-decoration-color: ${ANNOTATION_PURPLE};
  background: ${props => annotationBackground(props, props.$isActive)};
  cursor: pointer;
  transition: background 120ms ease-in-out;

  &:hover {
    background: ${props => annotationBackground(props, true)};
  }
`

const SuggestionInsert = styled.span`
  border-bottom: 2px solid ${ANNOTATION_PURPLE};
  background: ${props => annotationBackground(props, props.$isActive)};
  cursor: pointer;
  transition: background 120ms ease-in-out;

  &:hover {
    background: ${props => annotationBackground(props, true)};
  }
`

const CommentMark = styled.span`
  background: ${props => annotationBackground(props, props.$isActive)};
  border-bottom: 2px solid ${ANNOTATION_PURPLE};
  border-radius: 2px;
  cursor: pointer;
  transition: background 120ms ease-in-out;

  &:hover {
    background: ${props => annotationBackground(props, true)};
  }
`
